import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import { Button } from 'core/components';
import { AccessPermissions, FileType } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridTableContext, useNotificationContext } from 'core/states';
import { useJobActions } from 'jobs/hooks';
import { IJob } from 'jobs/types';
import { IProject } from 'projects/types';
import { Color, Variant } from 'theme/enums';

import { ExportEntityType } from './enum';

interface IProps {
  entityType: ExportEntityType;
  isExportLogAllowed: (selectedRows: IJob[] | IProject[]) => boolean;
}

export const ExportTimeLogButton = ({ entityType, isExportLogAllowed }: IProps) => {
  const { t } = useTranslation();

  const { hasPermission } = useUserPermissions();
  const { selectedRows, deselectAllRows } = useAgGridTableContext();
  const { clearNotification } = useNotificationContext();

  const { handleCliJobExport } = useCliJobHandler();
  const { exportJobLog } = useJobActions();

  const isDisabled = useMemo(
    () =>
      !(
        hasPermission(AccessPermissions.ACCESS_COMPANY_BOM_LOG) &&
        isExportLogAllowed(selectedRows) &&
        selectedRows.length === 1
      ),
    [hasPermission, isExportLogAllowed, selectedRows]
  );

  const onClick = async () => {
    handleCliJobExport(
      exportJobLog,
      {
        options: {
          fileType: FileType.XLS,
          filters: {
            [`${entityType}Id`]: selectedRows[0].id,
          },
        },
      },
      {
        exportInProgressMessage: t('messageExportJobLogInProgress'),
        onGenerateExportFinished: () => {
          clearNotification();
          deselectAllRows();
        },
      }
    );
  };

  return (
    <Button
      text={t('buttonLabelExportLog')}
      color={Color.PRIMARY}
      variant={isDisabled ? Variant.OUTLINED : Variant.SOLID}
      onClick={onClick}
      disabled={isDisabled}
    />
  );
};
